import { AxiosResponse } from "axios";
import laravelApi from "./api";
import { AuthError, NotFoundError } from "./error_service";
import {
  CreateNetSuiteCustomerPayload,
  CreateNetSuiteQuotePayload,
  FoundNetSuiteItemBySku,
  NetSuiteCustomer,
  NetSuiteEmployee,
  NetSuiteQuote,
  NetSuiteQuoteLineItem,
  PartialNetSuiteCustomer,
  PaymentTerm,
  PriceLevel
} from "@/models/netsuite/netsuite";

const netSuiteApi = laravelApi;

export default class NetSuiteService {
  public async getNetSuiteEmployeeId(userEmail: string): Promise<number> {
    try {
      let employee = {} as NetSuiteEmployee;
      const queryEmail = process.env.VUE_APP_TEST_REP_EMAIL
        ? process.env.VUE_APP_TEST_REP_EMAIL
        : userEmail;
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_employee_by_email/${queryEmail}`;
      const res: AxiosResponse = await netSuiteApi.get(netSuiteApiUrl);
      employee = res.data.rows[0];
      if (employee && employee.id) {
        return employee.id;
      } else
        throw new AuthError(
          "Unable to properly authenticate and connect. Please contact the administrator.",
          500
        );
    } catch (err) {
      throw new AuthError(err.response.data.message, err.response.status);
    }
  }

  public async getRepNetSuiteQuotes(
    repNetSuiteId: number
  ): Promise<NetSuiteQuote[]> {
    let quotes = [] as NetSuiteQuote[];
    try {
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_quotes_by_rep/${repNetSuiteId}/open`;
      const res: AxiosResponse = await netSuiteApi.get(netSuiteApiUrl);
      quotes = res.data.rows || [];
    } catch (err) {
      console.error("Error getting the rep's open NetSuite quotes", err);
    }
    return quotes;
  }

  public async getAllNetSuiteQuotes(): Promise<NetSuiteQuote[]> {
    let quotes = [] as NetSuiteQuote[];
    try {
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_quotes/open`;
      const res: AxiosResponse = await netSuiteApi.get(netSuiteApiUrl);
      quotes = res.data.rows || [];
    } catch (err) {
      console.error("Error getting the open NetSuite quotes", err);
    }
    return quotes;
  }
  public async getNetSuiteQuoteLineItemsById(
    quoteNetSuiteId: number
  ): Promise<NetSuiteQuoteLineItem[]> {
    let netSuiteQuoteLineItems = [] as NetSuiteQuoteLineItem[];
    try {
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_quote_lineitems/${quoteNetSuiteId}`;
      const res: AxiosResponse = await netSuiteApi.get(netSuiteApiUrl);
      netSuiteQuoteLineItems = res.data.rows;
      // console.log(
      //   "# of Open NetSuite Quote Line Items:",
      //   netSuiteQuoteLineItems.length
      // );
      // console.log("netSuiteQuoteLineItems", netSuiteQuoteLineItems);
    } catch (err) {
      console.error("Error getting NetSuite quote line items", err);
    }
    return netSuiteQuoteLineItems;
  }

  public async getCustomerNetSuiteQuotes(
    customerNetSuiteId: number
  ): Promise<NetSuiteQuote[]> {
    let quotes = [] as NetSuiteQuote[];
    try {
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_customer_quotes/${customerNetSuiteId}/open`;
      const res: AxiosResponse = await netSuiteApi.get(netSuiteApiUrl);
      quotes = res.data.rows;
    } catch (err) {
      console.error("Error getting the customer's open NetSuite quotes", err);
    }
    return quotes;
  }

  public async getPartialNetSuiteCustomers(
    repNetSuiteId?: number
  ): Promise<PartialNetSuiteCustomer[]> {
    let netSuiteCustomers = [] as PartialNetSuiteCustomer[];
    try {
      const netSuiteApiUrl = repNetSuiteId
        ? `/api/netsuite/suiteql/get_customers/${repNetSuiteId}`
        : `/api/netsuite/suiteql/get_customers`;
      const res: AxiosResponse = await netSuiteApi.get(netSuiteApiUrl);
      netSuiteCustomers = res.data;
    } catch (err) {
      console.error("Error getting the NetSuite customers", err);
    }
    return netSuiteCustomers;
  }

  public async getMyCustomers(): Promise<PartialNetSuiteCustomer[]> {
    let netSuiteCustomers = [] as PartialNetSuiteCustomer[];
    try {
      const res: AxiosResponse = await netSuiteApi.get("/api/netsuite/suiteql/get_my_customers");
      netSuiteCustomers = res.data;
    } catch (err) {
      console.error("Error getting my NetSuite customers", err);
    }
    return netSuiteCustomers;
  }

  public async getMyQuotes(): Promise<NetSuiteQuote[]> {
    let quotes = [] as NetSuiteQuote[];
    try {
      const res: AxiosResponse = await netSuiteApi.get("/api/netsuite/suiteql/get_my_quotes/open");
      quotes = res.data.rows || [];
    } catch (err) {
      console.error("Error getting my NetSuite quotes", err);
    }
    return quotes;
  }

  public async getNetSuiteCustomerById(
    customerNetSuiteId: string
  ): Promise<NetSuiteCustomer> {
    let netSuiteCustomer = {} as NetSuiteCustomer;
    try {
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_customer/${customerNetSuiteId}`;
      const res: AxiosResponse = await netSuiteApi.get(netSuiteApiUrl);
      netSuiteCustomer = res.data;
    } catch (err) {
      console.error("Error getting the NetSuite customer", err);
    }
    return netSuiteCustomer;
  }

  // Note: FNSI-101 - call this function when creating a new quote, determining if a temporary NetSuite item needs to be used for a custom SKU or if the found SKU can be used.
  public async getNetSuiteItemBySku(
    itemSku: string
  ): Promise<FoundNetSuiteItemBySku> {
    let foundNetSuiteItem = {} as FoundNetSuiteItemBySku;
    try {
      const netSuiteApiUrl = `/api/netsuite/inventory/get_item_by_sku/${encodeURIComponent(itemSku)}`;
      const res: AxiosResponse = await netSuiteApi.get(netSuiteApiUrl);
      if (!res.data || !res.data.rows || res.data.rows.length === 0) {
        throw new NotFoundError(
          `No NetSuite item found with the provided SKU: ${itemSku}`,
          404
        );
      } else if (res.data.rows.length > 1) {
        throw new NotFoundError(
          "Multiple items found with the provided SKU.",
          404
        );
      }
      foundNetSuiteItem = res.data.rows[0];
    } catch (err) {
      console.error(err.message);
    }
    return foundNetSuiteItem;
  }

  public async createNetSuiteCustomer(
    payload: CreateNetSuiteCustomerPayload
  ): Promise<any> {
    const netSuiteApiUrl = `/api/netsuite/suiteql/create_customer`;
    try {
      const res: AxiosResponse = await netSuiteApi.post(
        netSuiteApiUrl,
        payload
      );
      return res.data;
    } catch (err) {
      const e = err as any;
      console.error(
        "[NetSuiteService] createNetSuiteCustomer error:",
        e?.response?.data ?? e
      );
      throw new Error(
        e?.response?.data?.message ??
          e?.message ??
          "Unknown error creating NetSuite customer."
      );
    }
  }

  // ToDo: FNSI-101 - new POST endpoint to Laravel API to create new NetSuite quote
  public async createNetSuiteQuote(
    payload: CreateNetSuiteQuotePayload
  ): Promise<any> {
    const netSuiteApiUrl = `/api/netsuite/suiteql/create_quote`;
    try {
      const res: AxiosResponse = await netSuiteApi.post(
        netSuiteApiUrl,
        payload
      );
      return res.data;
    } catch (err) {
      const e = err as any;
      console.error(
        "[NetSuiteService] createNetSuiteQuote error:",
        e?.response?.data ?? e
      );
      throw new Error(
        e?.response?.data?.message ??
          e?.message ??
          "Unknown error creating NetSuite quote."
      );
    }
  }

  public async sendQuoteEmail(quoteId: number): Promise<void> {
    const netSuiteApiUrl = `/api/netsuite/suiteql/send_quote_email/${quoteId}`;
    await netSuiteApi.post(netSuiteApiUrl);
  }

  public async getQuotePdf(quoteId: number): Promise<Blob> {
    const netSuiteApiUrl = `/api/netsuite/suiteql/get_quote_pdf/${quoteId}`;
    const res: AxiosResponse = await netSuiteApi.get(netSuiteApiUrl, {
      responseType: "blob"
    });
    return res.data;
  }

  public async getPriceLevels(): Promise<PriceLevel[]> {
    let paymentTerms = [] as PriceLevel[];
    try {
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_price_levels`;
      const res: AxiosResponse = await netSuiteApi.get(netSuiteApiUrl);
      paymentTerms = res.data;
    } catch (err) {
      console.error("Error getting the price levels", err);
    }
    return paymentTerms;
  }

  public async getPaymentTerms(): Promise<PaymentTerm[]> {
    let paymentTerms = [] as PaymentTerm[];
    try {
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_payment_terms`;
      const res: AxiosResponse = await netSuiteApi.get(netSuiteApiUrl);
      paymentTerms = res.data;
    } catch (err) {
      console.error("Error getting the payment terms", err);
    }
    return paymentTerms;
  }
}
